import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
	getProxies,
	updateProxy as updateProxyApi,
	removeProxy as removeProxyApi,
	addProxy as addProxyApi,
	toggleStatus,
	getStats as getManagerStats,
	clearStats as clearManagerStats,
} from '@/api/proxy'
import IApiResponse from '@/types/api-response'
import IList from '@/types/list-result'
import IPagination from '@/types/pagination'

interface ProxyState {
	proxies: IProxy[]
	loading: boolean
	error: string | null
	pagination?: IPagination
	stats: { [key: string]: any }
	fetching_stats: boolean
	stats_clearing: boolean

	updating: boolean
}

export interface IProxy {
	_id: string
	name: string
	host: string
	port: number
	username?: string
	password?: string
	is_active: boolean
	note?: string
	updated_date?: Date
	updated_by?: string
	created_date: Date
	created_by: string
	allow_get_time: boolean
	allow_bid: boolean
	allow_get_history: boolean
	allow_search: boolean
	allow_get_products_in_cate: boolean
	allow_get_detail: boolean
	allow_fetch_success: boolean
	allow_get_related: boolean
	tags: string[]
	weight: number
}

const initialState: ProxyState = {
	loading: false,
	proxies: [],
	error: null,
	stats: {},
	fetching_stats: false,
	stats_clearing: false,
	updating: false,
}

export const fetchProxies = createAsyncThunk<IApiResponse<IList<IProxy>>, any>(
	'proxy/fetch_proxies',
	async (params: any) => {
		return await getProxies(params)
	}
)

export const updateProxy = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('proxy/update_proxy', async ({ params, cb }: any) => {
	try {
		const result = await updateProxyApi(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error) {
		if (typeof cb === 'function') {
			cb({ success: false })
		}

		throw error
	}
})

export const removeProxy = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('proxy/remove_proxy', async ({ params, cb }: any) => {
	try {
		const result = await removeProxyApi(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error) {
		if (typeof cb === 'function') {
			cb({ success: false })
		}

		throw error
	}
})

export const addProxy = createAsyncThunk<IApiResponse<null | undefined>, any>(
	'proxy/add_proxy',
	async ({ params, cb }: any) => {
		try {
			const result = await addProxyApi(params)

			if (typeof cb === 'function') {
				cb(result)
			}

			return result
		} catch (error) {
			if (typeof cb === 'function') {
				cb({ success: false })
			}

			throw error
		}
	}
)

export const batchUpdateStatus = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('proxy/batch_update_status', async ({ params, cb }: any) => {
	try {
		const result = await toggleStatus(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error) {
		if (typeof cb === 'function') {
			cb({ success: false })
		}

		throw error
	}
})

export const getStats = createAsyncThunk<
	IApiResponse<null | undefined | { [key: string]: any }>
>('proxy/get_stats', async () => {
	try {
		const result = await getManagerStats()
		return result
	} catch (error) {
		throw error
	}
})

export const clearStats = createAsyncThunk<
	IApiResponse<null | undefined>,
    any
>('proxy/clear_stats', async ({ cb }: { cb?: (r: IApiResponse<null | undefined>) => void }) => {
	try {
		const result = await clearManagerStats()
        if (typeof cb === 'function') {
            cb(result)
        }
		return result
	} catch (error) {
		throw error
	}
})

const proxySlice = createSlice({
	name: 'proxy',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchProxies.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(fetchProxies.fulfilled, (state, { payload }) => {
				state.loading = false
				state.pagination = payload.data!.pagination
				if (payload.success) {
					state.proxies = payload.data!.records
				} else {
					state.proxies = []
				}
			})
			.addCase(fetchProxies.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message || 'Something went wrong'
			})

			.addCase(updateProxy.pending, (state) => {
				state.updating = true
			})
			.addCase(updateProxy.fulfilled, (state) => {
				state.updating = false
			})
			.addCase(updateProxy.rejected, (state, action) => {
				state.updating = false
				state.error = action.error.message || 'Something went wrong'
			})

			.addCase(getStats.pending, (state) => {
				state.fetching_stats = true
			})
			.addCase(getStats.fulfilled, (state, { payload }) => {
				state.fetching_stats = false
				if (payload?.success) {
					state.stats = payload.data as { [key: string]: any }
				}
			})
			.addCase(getStats.rejected, (state) => {
				state.fetching_stats = false
			})

			.addCase(clearStats.pending, (state) => {
				state.stats_clearing = true
			})
			.addCase(clearStats.fulfilled, (state) => {
				state.stats_clearing = false
			})
			.addCase(clearStats.rejected, (state) => {
				state.stats_clearing = false
			})
	},
})

export default proxySlice.reducer
